import React, { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '../Sandbox/Sandbox.module.css';
import globalStyles from '../../App.module.css';
import Board from '../Board';
import { GameContext } from '../Providers/GameContext';
import { useTheme } from '../Providers/ThemeContext';
import { useLanguage } from '../Providers/LanguageContext';
import GlobalHeader from '../GlobalHeader';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import WispyStars from '../Tutorials/WispyStars';
import TurnPopover from '../TurnPopover/TurnPopover';
import { PieceType, Mode, IPiece, Position } from '../../services/zodiacEngine/types';
import PiecePalette, { PiecePaletteRef } from '../Sandbox/PiecePalette';
import ZodiacEngineService from '../ZodiacEngineService';
import { Piece } from '../../services/zodiacEngine/Piece';
import { ZodiacFEN } from '../../services/zodiacEngine/Fen/ZodiacFEN';
import { savePuzzle, updatePuzzle, deletePuzzle, getPuzzlesByType, SavedPuzzle, MoveStep, MoveRound } from '../../services/firebase/puzzleService';
import { playUISound } from '../../utils/UISoundEffects';

function PuzzleDesigner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const {
    gameState,
    potentialMoves,
    potentialSideEffects,
    updateGameState
  } = useContext(GameContext);
  const piecePaletteRef = useRef<PiecePaletteRef>(null);
  const [replayLoaded, setReplayLoaded] = useState(false);
  const [puzzleType, setPuzzleType] = useState<'bestMove' | 'kingGuesser'>('bestMove');
  const [playerIsLight, setPlayerIsLight] = useState(true);
  const [lightKingType, setLightKingType] = useState<string | null>(null);
  const [darkKingType, setDarkKingType] = useState<string | null>(null);
  const [kingAssignMode, setKingAssignMode] = useState<'light' | 'dark' | null>(null);
  // Multi-move best move state
  const [totalMoves, setTotalMoves] = useState(1);
  const [moveSequence, setMoveSequence] = useState<(Partial<MoveRound>)[]>([{}]);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [activeStepTarget, setActiveStepTarget] = useState<'playerFrom' | 'playerTo' | 'opponentFrom' | 'opponentTo' | 'altFrom' | 'altTo' | null>(null);
  const [activeAltIndex, setActiveAltIndex] = useState<number>(-1);
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [puzzleName, setPuzzleName] = useState('');
  const [savedPuzzles, setSavedPuzzles] = useState<SavedPuzzle[]>([]);
  const [loadingPuzzles, setLoadingPuzzles] = useState(false);
  const [editingPuzzleId, setEditingPuzzleId] = useState<string | null>(null);
  const [deletingPuzzleId, setDeletingPuzzleId] = useState<string | null>(null);
  const [previewMoveIndex, setPreviewMoveIndex] = useState<number>(-1);
  const [startingFen, setStartingFen] = useState<string | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string>('');
  const [mobileView, setMobileView] = useState<'board' | 'designer' | 'saved'>('board');

  // Fetch saved puzzles when puzzle type changes
  const fetchSavedPuzzles = async () => {
    setLoadingPuzzles(true);
    try {
      const puzzles = await getPuzzlesByType(puzzleType);
      setSavedPuzzles(puzzles);
    } catch (error) {
    }
    setLoadingPuzzles(false);
  };

  useEffect(() => {
    fetchSavedPuzzles();
    setEditingPuzzleId(null);
    setDeletingPuzzleId(null);
  }, [puzzleType]);

  // Initialize sandbox mode when component mounts
  useEffect(() => {
    const engine = ZodiacEngineService.zodiacEngine;

    // Check if we're loading a replay position
    const takeover = (location.state as any)?.replayTakeover;
    if (takeover?.fen) {
      engine.loadFromFEN(takeover.fen);
      setReplayLoaded(true);
      // Clear router state to prevent re-triggering
      window.history.replaceState({}, document.title);
    } else {
      // Clear the board to ensure no pieces remain from previous session
      engine.loadFromFEN('8/8/8/8/8');
    }

    // Set sandbox mode AFTER loadFromFEN, since loadFromFEN resets mode to Dev
    engine.setGameMode(Mode.Sandbox);
  }, []);

  // After replay FEN loads, mark imported pieces as placed in the palette
  useEffect(() => {
    if (!replayLoaded || !piecePaletteRef.current) return;

    const boardPieces: Array<{type: PieceType, isLight: boolean}> = [];
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;
        pieces.forEach((piece: any) => {
          boardPieces.push({ type: piece.type, isLight: piece.isLight });
        });
      }
    }

    if (boardPieces.length > 0) {
      piecePaletteRef.current.markBoardPiecesAsPlaced(boardPieces);
      setReplayLoaded(false);
    }
  }, [replayLoaded, gameState]);

  // Track previous board pieces to detect removals (captures, virus kills, etc.)
  const [previousPieces, setPreviousPieces] = useState<Map<string, { type: PieceType; isLight: boolean }>>(new Map());

  // Monitor for piece removals in Sandbox mode and return pieces to palette
  useEffect(() => {
    if (gameState.mode !== Mode.Sandbox) return;

    // Collect all current piece IDs and their info from the board
    const currentPieces = new Map<string, { type: PieceType; isLight: boolean }>();
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;
        pieces.forEach((piece: any) => {
          currentPieces.set(piece.id, { type: piece.type, isLight: piece.isLight });
        });
      }
    }

    // Only check for removals if we have a previous state (not initial load)
    if (previousPieces.size > 0) {
      previousPieces.forEach((pieceInfo, prevId) => {
        if (!currentPieces.has(prevId) && piecePaletteRef.current) {
          // This piece was removed - return it to the palette
          // Skip viruses as they're not meant to be returned
          if (pieceInfo.type !== PieceType.Virus) {
            piecePaletteRef.current.returnCapturedPiece(
              pieceInfo.type,
              pieceInfo.isLight
            );
          }
        }
      });
    }

    // Update previous state for next comparison
    setPreviousPieces(currentPieces);
  }, [gameState]);

  const handleHome = () => {
    navigate('/game');
  };

  // Validate if a piece can be placed on a specific square in sandbox mode
  const isValidSandboxPlacement = (piece: any, targetSquare: any, position: any): boolean => {
    const existingPieces = targetSquare.Pieces || [];
    const hasEnemyPiece = existingPieces.some((p: any) => p.isLight !== piece.isLight);
    const hasFriendlyPiece = existingPieces.some((p: any) => p.isLight === piece.isLight);

    // VIRUS SPECIAL CASE: Virus can be placed on empty squares OR on animal pieces
    if (piece.type === 'Virus') {
      const animalTypes = ['Bear', 'Crane', 'Frog', 'Goat', 'Monkey', 'Octopus', 'Penguin', 'Rhino', 'Snake', 'Wolf'];

      // Check if there's already a virus on this square
      const hasVirus = existingPieces.some((p: any) => p.type === 'Virus');
      if (hasVirus) return false;

      // Empty square is allowed
      if (existingPieces.length === 0) return true;

      // If there are pieces, only allow if there's an animal (virus can infect animals)
      return existingPieces.some((p: any) => animalTypes.includes(p.type));
    }

    // Can't place on a square with a friendly piece (except trees)
    if (hasFriendlyPiece) {
      const friendlyTree = existingPieces.find((p: any) => p.type === 'Tree' && p.isLight === piece.isLight);
      if (!friendlyTree) return false;

      // If there's a friendly tree, check if this piece can climb trees
      const canClimb = ['Bear', 'Crane', 'Frog', 'Fungi', 'Goat', 'Monkey', 'Octopus', 'Snake'].includes(piece.type);
      if (!canClimb) return false;
    }

    // Check if there's an enemy piece - allow climbing enemy trees in sandbox mode
    if (hasEnemyPiece) {
      const enemyTree = existingPieces.find((p: any) => p.type === 'Tree' && p.isLight !== piece.isLight);

      if (enemyTree) {
        const canClimb = ['Bear', 'Crane', 'Frog', 'Fungi', 'Goat', 'Monkey', 'Octopus', 'Snake'].includes(piece.type);
        if (!canClimb) return false;

        // Check if tree already has a piece in it
        if (existingPieces.length > 1) return false;
      } else {
        return false;
      }
    }

    return true;
  };

  const handlePieceDragStart = (event: React.DragEvent<HTMLImageElement>, piece: IPiece, position: Position) => {
    event.dataTransfer.setData("piece", JSON.stringify({
      type: piece.type,
      isLight: piece.isLight,
      isFromBoard: true,
      fromRow: position.row,
      fromCol: position.col,
      pieceId: piece.id,
    }));
    event.dataTransfer.effectAllowed = "move";

    // Custom drag image using an offscreen DOM element (must be in DOM for setDragImage)
    const element = event.currentTarget;
    const size = Math.round(Math.max(element.offsetWidth, element.offsetHeight) * 0.85);
    const dragEl = document.createElement('div');
    dragEl.style.position = 'fixed';
    dragEl.style.top = '-1000px';
    dragEl.style.left = '-1000px';
    dragEl.style.width = `${size}px`;
    dragEl.style.height = `${size}px`;
    dragEl.style.pointerEvents = 'none';
    dragEl.style.zIndex = '-1';

    const img = document.createElement('img');
    img.src = element.src;
    img.style.width = `${size}px`;
    img.style.height = `${size}px`;
    dragEl.appendChild(img);
    document.body.appendChild(dragEl);

    event.dataTransfer.setDragImage(dragEl, size / 2, size / 2);

    // Hide the original piece on the board during drag
    element.style.opacity = '0';
    element.addEventListener('dragend', () => {
      element.style.opacity = '';
    }, { once: true });

    requestAnimationFrame(() => {
      document.body.removeChild(dragEl);
    });
  };

  const handleBoardPieceDroppedToSandbox = (pieceType: PieceType, isLight: boolean, fromRow: number, fromCol: number, pieceId: string) => {
    const sourceSquare = gameState.board[fromRow][fromCol];
    const pieceIndex = sourceSquare.Pieces.findIndex((p: any) => p.id === pieceId);
    if (pieceIndex === -1) return;
    sourceSquare.Pieces.splice(pieceIndex, 1);

    // Force re-render so the board reflects the removal
    updateGameState({ ...gameState });

    const audio = new Audio('/sand1.mp3');
    audio.volume = 0.3;
    audio.play().catch(() => {});
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, position: any) => {
    event.preventDefault();
    const pieceData = event.dataTransfer.getData("piece");

    if (!pieceData) return;

    const parsedData = JSON.parse(pieceData);
    const { type, isLight, sandboxKey, isRepositioning, isFromBoard, fromRow, fromCol, pieceId } = parsedData;

    // Handle board-to-board piece repositioning
    if (isFromBoard) {
      if (fromRow === position.row && fromCol === position.col) return;

      const sourceSquare = gameState.board[fromRow][fromCol];
      const pieceIndex = sourceSquare.Pieces.findIndex((p: any) => p.id === pieceId);
      if (pieceIndex === -1) return;

      const piece = sourceSquare.Pieces[pieceIndex];
      const targetSquare = gameState.board[position.row][position.col];

      if (!isValidSandboxPlacement(piece, targetSquare, position)) return;

      const fromAlgebraic = String.fromCharCode(65 + fromCol) + (fromRow + 1);
      sourceSquare.Pieces.splice(pieceIndex, 1);
      // Place at destination via engine (this adds a ~{placement:...} annotation)
      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      // Replace the placement annotation with a moved annotation in the last FEN entry
      const toAlgebraic = String.fromCharCode(65 + position.col) + (position.row + 1);
      const colorStr = piece.isLight ? 'Light' : 'Dark';
      const lastIdx = gameState.fenHistory.length - 1;
      if (lastIdx >= 0) {
        gameState.fenHistory[lastIdx] = gameState.fenHistory[lastIdx]
          .replace(/~\{placement:[^}]+\}/, `~{moved:${piece.type},${colorStr},${fromAlgebraic},${toAlgebraic}}`);
      }

      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);
      return;
    }

    // If this is a repositioning drag, check if it's dropped on the sandbox area
    if (isRepositioning) {
      const sandboxContainer = document.querySelector('[class*="paletteForeground"]');
      if (sandboxContainer) {
        const rect = sandboxContainer.getBoundingClientRect();
        const isOverSandbox = event.clientX >= rect.left &&
                             event.clientX <= rect.right &&
                             event.clientY >= rect.top &&
                             event.clientY <= rect.bottom;

        if (isOverSandbox) return;
      }
    }

    // Check if a piece of this type and color is already on the board (Sandbox mode only)
    if (gameState.mode === Mode.Sandbox && piecePaletteRef.current) {
      if (piecePaletteRef.current.isPieceOnBoard(type as PieceType, isLight)) {
        if (sandboxKey && piecePaletteRef.current) {
          piecePaletteRef.current.handlePieceDropFailed(sandboxKey);
        }
        return;
      }
    }

    // Place the piece on the board in sandbox mode
    let placementSuccessful = false;

    try {
      if (gameState.mode !== Mode.Sandbox) {
        ZodiacEngineService.zodiacEngine.setGameMode(Mode.Sandbox);
      }

      const piece = new Piece(type as PieceType, isLight);

      if (type === 'Penguin') {
        piece.direction = isLight ? 'down' : 'up';
      }

      const targetSquare = gameState.board[position.row][position.col];
      const canPlace = isValidSandboxPlacement(piece, targetSquare, position);

      if (!canPlace) {
        throw new Error(`Cannot place ${piece.type} at this location - invalid placement`);
      }

      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      // VIRUS INFECTION: If placing a virus on an animal, mark the animal as infected
      if (type === 'Virus') {
        const square = gameState.board[position.row][position.col];
        const animalPiece = square.Pieces.find((p: any) => p.type !== 'Virus');
        if (animalPiece) {
          animalPiece.isInfected = true;
        }
      }

      placementSuccessful = true;

      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);

    } catch (error) {
      placementSuccessful = false;
    }

    if (sandboxKey && piecePaletteRef.current) {
      if (placementSuccessful) {
        piecePaletteRef.current.handlePiecePlaced(sandboxKey);
      } else {
        piecePaletteRef.current.handlePieceDropFailed(sandboxKey);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  // Handle board square clicks — king assignment, move selection, or tap-to-place
  const handleSquareClick = (position: any) => {
    const square = gameState.board[position.row][position.col];
    const topPiece = square.Pieces.length > 0 ? square.Pieces[square.Pieces.length - 1] : null;

    // King assignment mode — click a piece to assign it as king
    if (kingAssignMode) {
      if (!topPiece) return; // Must click a piece
      if (kingAssignMode === 'light' && !topPiece.isLight) return; // Must be correct side
      if (kingAssignMode === 'dark' && topPiece.isLight) return;
      if (kingAssignMode === 'light') {
        setLightKingType(topPiece.type);
      } else {
        setDarkKingType(topPiece.type);
      }
      setKingAssignMode(null);
      return;
    }

    // Multi-move step assignment — assign piece/square to move sequence
    if (activeStepTarget) {
      const idx = activeStepIndex;
      const seq = [...moveSequence];
      if (!seq[idx]) seq[idx] = {};

      if (activeStepTarget === 'playerFrom') {
        if (!topPiece) return; // Must click a piece
        seq[idx] = {
          ...seq[idx],
          playerMove: {
            ...(seq[idx].playerMove || { from: { row: 0, col: 0 }, to: { row: 0, col: 0 }, pieceType: '', pieceIsLight: true }),
            pieceType: topPiece.type,
            pieceIsLight: topPiece.isLight,
            from: { row: position.row, col: position.col },
          },
        };
        setMoveSequence(seq);
        setActiveStepTarget('playerTo'); // auto-advance to destination
        return;
      }
      if (activeStepTarget === 'playerTo') {
        seq[idx] = {
          ...seq[idx],
          playerMove: {
            ...(seq[idx].playerMove || { from: { row: 0, col: 0 }, pieceType: '', pieceIsLight: true }),
            to: { row: position.row, col: position.col },
          } as MoveStep,
        };
        setMoveSequence(seq);
        setActiveStepTarget(null);
        return;
      }
      if (activeStepTarget === 'opponentFrom') {
        if (!topPiece) return;
        seq[idx] = {
          ...seq[idx],
          opponentResponse: {
            ...(seq[idx].opponentResponse || { from: { row: 0, col: 0 }, to: { row: 0, col: 0 }, pieceType: '', pieceIsLight: true }),
            pieceType: topPiece.type,
            pieceIsLight: topPiece.isLight,
            from: { row: position.row, col: position.col },
          },
        };
        setMoveSequence(seq);
        setActiveStepTarget('opponentTo');
        return;
      }
      if (activeStepTarget === 'opponentTo') {
        seq[idx] = {
          ...seq[idx],
          opponentResponse: {
            ...(seq[idx].opponentResponse || { from: { row: 0, col: 0 }, pieceType: '', pieceIsLight: true }),
            to: { row: position.row, col: position.col },
          } as MoveStep,
        };
        setMoveSequence(seq);
        setActiveStepTarget(null);
        return;
      }
      if (activeStepTarget === 'altFrom') {
        if (!topPiece) return; // Must click a piece
        const alts = [...(seq[idx].alternativePlayerMoves || [])];
        alts[activeAltIndex] = {
          ...(alts[activeAltIndex] || { from: { row: 0, col: 0 }, to: { row: 0, col: 0 }, pieceType: '', pieceIsLight: true }),
          pieceType: topPiece.type,
          pieceIsLight: topPiece.isLight,
          from: { row: position.row, col: position.col },
        };
        seq[idx] = { ...seq[idx], alternativePlayerMoves: alts };
        setMoveSequence(seq);
        setActiveStepTarget('altTo'); // auto-advance to destination
        return;
      }
      if (activeStepTarget === 'altTo') {
        const alts = [...(seq[idx].alternativePlayerMoves || [])];
        alts[activeAltIndex] = {
          ...(alts[activeAltIndex] || { from: { row: 0, col: 0 }, pieceType: '', pieceIsLight: true }),
          to: { row: position.row, col: position.col },
        } as MoveStep;
        seq[idx] = { ...seq[idx], alternativePlayerMoves: alts };
        setMoveSequence(seq);
        setActiveStepTarget(null);
        return;
      }
    }

    // Default: tap-to-place from palette
    if (!piecePaletteRef.current) return;

    const selectedPiece = piecePaletteRef.current.getSelectedPiece();
    if (!selectedPiece) return;

    if (piecePaletteRef.current.isPieceOnBoard(selectedPiece.type, selectedPiece.isLight)) return;

    try {
      if (gameState.mode !== Mode.Sandbox) {
        ZodiacEngineService.zodiacEngine.setGameMode(Mode.Sandbox);
      }

      const piece = new Piece(selectedPiece.type, selectedPiece.isLight);

      if (selectedPiece.type === 'Penguin') {
        piece.direction = selectedPiece.isLight ? 'down' : 'up';
      }

      const targetSquare = gameState.board[position.row][position.col];
      const canPlace = isValidSandboxPlacement(piece, targetSquare, position);
      if (!canPlace) return;

      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      if (selectedPiece.type === 'Virus') {
        const sqr = gameState.board[position.row][position.col];
        const animalPiece = sqr.Pieces.find((p: any) => p.type !== 'Virus');
        if (animalPiece) {
          animalPiece.isInfected = true;
        }
      }

      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);

      const suffix = selectedPiece.isLight ? 'light' : 'dark';
      const pieceKey = `${selectedPiece.type}-${suffix}-0`;
      piecePaletteRef.current.handlePiecePlaced(pieceKey);
      piecePaletteRef.current.clearSelectedPiece();

      // Keep startingFen in sync when editing at Start position
      if (startingFen && previewMoveIndex === -1) {
        setTimeout(() => {
          setStartingFen(ZodiacFEN.encodeWithoutKings(ZodiacEngineService.zodiacEngine.getGameState()));
        }, 50);
      }

    } catch (error) {
    }
  };

  const handleClearBoard = () => {
    const board = gameState.board;
    const piecesToReturn: Array<{type: PieceType, isLight: boolean}> = [];

    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const pieces = board[row][col].Pieces;
        pieces.forEach((piece: any) => {
          piecesToReturn.push({
            type: piece.type,
            isLight: piece.isLight
          });
        });
        board[row][col].Pieces = [];
      }
    }

    ZodiacEngineService.zodiacEngine.loadFromFEN('8/8/8/8/8');

    if (piecePaletteRef.current) {
      piecesToReturn.forEach((pieceInfo) => {
        piecePaletteRef.current?.returnCapturedPiece(pieceInfo.type, pieceInfo.isLight);
      });
    }

    const audio = new Audio('/sand1.mp3');
    audio.volume = 0.4;
    audio.play().catch(console.error);
  };

  const toAlgebraic = (row: number, col: number) => String.fromCharCode(65 + col) + (row + 1);

  const isMoveStepComplete = (step?: Partial<MoveStep>): step is MoveStep => {
    return !!step && !!step.pieceType && !!step.from && !!step.to;
  };

  const canSave = (): boolean => {
    if (!puzzleName.trim()) return false;
    if (!lightKingType || !darkKingType) return false;
    // Both puzzle types require move sequences
    for (let i = 0; i < totalMoves; i++) {
      const round = moveSequence[i];
      if (!round || !isMoveStepComplete(round.playerMove)) return false;
      // Best Move: all rounds except the last need an opponent response
      if (puzzleType === 'bestMove' && i < totalMoves - 1 && !isMoveStepComplete(round.opponentResponse)) return false;
      // Alternative moves, if present, must all be complete
      if (round.alternativePlayerMoves) {
        for (const alt of round.alternativePlayerMoves) {
          if (!isMoveStepComplete(alt)) return false;
        }
      }
    }
    // Check board has pieces
    let hasPieces = false;
    for (let row = 0; row < gameState.board.length && !hasPieces; row++) {
      for (let col = 0; col < gameState.board[row].length && !hasPieces; col++) {
        if (gameState.board[row][col].Pieces.length > 0) hasPieces = true;
      }
    }
    return hasPieces;
  };

  const isSectionModified = (index: number): boolean => {
    if (!savedSnapshot) return false;
    try {
      const saved = JSON.parse(savedSnapshot);
      return JSON.stringify(moveSequence[index]) !== JSON.stringify(saved[index]);
    } catch { return false; }
  };

  const handleLoadPuzzle = (puzzle: SavedPuzzle) => {
    const engine = ZodiacEngineService.zodiacEngine;

    // Reset palette tracking so the new board's pieces can be marked fresh
    if (piecePaletteRef.current) {
      piecePaletteRef.current.resetAllPlaced();
    }

    // Load puzzle FEN onto board
    engine.loadFromFEN(puzzle.fen);
    engine.setGameMode(Mode.Sandbox);
    setStartingFen(puzzle.fen);
    setPreviewMoveIndex(-1);

    // Reuse existing effect (replayLoaded) to sync palette with new board
    setReplayLoaded(true);

    // Restore all form state
    setPuzzleName(puzzle.name);
    setPlayerIsLight(puzzle.playerIsLight);
    setLightKingType(puzzle.lightKing);
    setDarkKingType(puzzle.darkKing);
    setTotalMoves(puzzle.totalMoves || 1);
    setMoveSequence(puzzle.moveSequence || [{}]);
    setSavedSnapshot(JSON.stringify((puzzle.moveSequence || [{}]).slice(0, puzzle.totalMoves || 1)));

    // Switch puzzle type if needed (triggers savedPuzzles refetch)
    if (puzzle.type !== puzzleType) {
      setPuzzleType(puzzle.type);
    }

    // Enter edit mode
    setEditingPuzzleId(puzzle.id);

    // Clear transient UI state
    setActiveStepTarget(null);
    setKingAssignMode(null);
    setSaveStatus(null);
    setDeletingPuzzleId(null);
  };

  const applyMoveWithShed = (board: any[][], move: { from: Position; to: Position }) => {
    const fromSq = board[move.from.row][move.from.col];
    const toSq = board[move.to.row][move.to.col];
    if (fromSq.Pieces.length === 0) return;

    // Check if capturing a snake with shed available
    if (toSq.Pieces.length > 0) {
      const shedSnake = toSq.Pieces.find(
        (p: IPiece) => p.type === PieceType.Snake && !p.hasShed
      );
      if (shedSnake) {
        shedSnake.hasShed = true;
        shedSnake.isEntangled = false;
        shedSnake.entangledPieceId = undefined;
        shedSnake.poisonedStatus = { isPoisoned: false, removeOnTurn: undefined };
        shedSnake.isInfected = false;
        shedSnake.zombifiedStatus = { isZombified: false, removeOnTurn: undefined };
        shedSnake.isZombified = false;

        const respawnRow = shedSnake.isLight ? 0 : 7;
        const respawnSq = board[respawnRow][move.to.col];
        const canRespawn = respawnSq.Pieces.length === 0 ||
          (respawnSq.Pieces.length === 1 && respawnSq.Pieces[0].type === PieceType.Tree);
        if (canRespawn) {
          respawnSq.Pieces.push(shedSnake);
        }
      }
    }

    toSq.Pieces = [fromSq.Pieces[0]];
    fromSq.Pieces = [];
  };

  const previewPosition = (moveIndex: number) => {
    if (!startingFen) return;
    setPreviewMoveIndex(moveIndex);

    const engine = ZodiacEngineService.zodiacEngine;

    // Always start from the initial FEN
    engine.loadFromFEN(startingFen);
    engine.setGameMode(Mode.Sandbox);

    if (moveIndex === -1) return; // Starting position — already loaded

    // Simulate moves 0..moveIndex (with snake shed support)
    for (let i = 0; i <= moveIndex; i++) {
      const round = moveSequence[i];
      if (!round) break;

      const pm = round.playerMove;
      if (pm?.from && pm?.to) {
        applyMoveWithShed(engine.getGameState().board, pm);
      }

      const opp = round.opponentResponse;
      if (opp?.from && opp?.to) {
        applyMoveWithShed(engine.getGameState().board, opp);
      }
    }

    // Re-encode and reload to sync the display
    const gs = engine.getGameState();
    const newFen = ZodiacFEN.encodeWithoutKings(gs);
    engine.loadFromFEN(newFen);
    engine.setGameMode(Mode.Sandbox);
  };

  const handleDeletePuzzle = async (puzzleId: string) => {
    try {
      await deletePuzzle(puzzleId);

      // If we were editing the deleted puzzle, exit edit mode
      if (editingPuzzleId === puzzleId) {
        setEditingPuzzleId(null);
      }

      setDeletingPuzzleId(null);
      setSaveStatus({ type: 'success', message: 'Puzzle deleted' });
      fetchSavedPuzzles();
    } catch (error: any) {
      setSaveStatus({ type: 'error', message: error.message || 'Failed to delete' });
      setDeletingPuzzleId(null);
    }
  };

  const handleNewPuzzle = () => {
    setEditingPuzzleId(null);
    setPuzzleName('');
    setLightKingType(null);
    setDarkKingType(null);
    setTotalMoves(1);
    setMoveSequence([{}]);
    setActiveStepTarget(null);
    setKingAssignMode(null);
    setSaveStatus(null);
  };

  const handleSave = async () => {
    if (!canSave()) return;
    setSaveStatus(null);

    try {
      // Use startingFen when previewing a non-start position to avoid saving the previewed board
      const fen = (startingFen && previewMoveIndex !== -1)
        ? startingFen
        : ZodiacFEN.encodeWithoutKings(gameState);

      const puzzleData: any = {
        name: puzzleName.trim(),
        type: puzzleType,
        fen,
        playerIsLight,
        lightKing: lightKingType!,
        darkKing: darkKingType!,
        createdAt: Date.now(),
      };

      // Save move sequence for both puzzle types
      puzzleData.totalMoves = totalMoves;
      puzzleData.moveSequence = moveSequence.slice(0, totalMoves);
      // Backward compat: also write legacy single-move fields for first move
      if (puzzleType === 'bestMove') {
        const firstMove = moveSequence[0]?.playerMove;
        if (firstMove) {
          puzzleData.movePieceType = firstMove.pieceType;
          puzzleData.movePieceIsLight = firstMove.pieceIsLight;
          puzzleData.moveFrom = firstMove.from;
          puzzleData.moveTo = firstMove.to;
        }
      }

      if (editingPuzzleId) {
        await updatePuzzle(editingPuzzleId, puzzleData);
        setSaveStatus({ type: 'success', message: `Updated! ID: ${editingPuzzleId}` });
      } else {
        const docId = await savePuzzle(puzzleData);
        setSaveStatus({ type: 'success', message: `Saved! ID: ${docId}` });
        setEditingPuzzleId(docId);
      }

      // Update saved state
      setStartingFen(fen);
      setSavedSnapshot(JSON.stringify(moveSequence.slice(0, totalMoves)));
      if (previewMoveIndex !== -1) {
        previewPosition(-1);
      }
      fetchSavedPuzzles();
    } catch (error: any) {
      setSaveStatus({ type: 'error', message: error.message || 'Failed to save' });
    }
  };

  return (
    <AdSenseLayout>
      <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app} ${styles.puzzleDesignerPage}`}>
        <WispyStars />
        <GlobalHeader />

        {/* Home button - top left */}
        <button
          className={styles.homeBackButton}
          onClick={() => { playUISound(); handleHome(); }}
          aria-label="Back to dev"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        {/* Mobile-only back button */}
      <button
        className={styles.mobileBackButton}
        onClick={() => { playUISound(); handleHome(); }}
        aria-label="Back to dev"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>

      {/* Mobile-only view toggle buttons */}
      <div className={styles.mobileViewToggles}>
        <button
          className={`${styles.mobileToggleButton} ${mobileView === 'board' ? styles.mobileToggleActive : ''}`}
          onClick={() => setMobileView('board')}
          aria-label="Board view"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
        </button>
        <button
          className={`${styles.mobileToggleButton} ${mobileView === 'designer' ? styles.mobileToggleActive : ''}`}
          onClick={() => setMobileView('designer')}
          aria-label="Designer view"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
          </svg>
        </button>
        <button
          className={`${styles.mobileToggleButton} ${mobileView === 'saved' ? styles.mobileToggleActive : ''}`}
          onClick={() => setMobileView('saved')}
          aria-label="Saved puzzles"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
        </button>
      </div>

      <TurnPopover
        isThisPlayersTurn={gameState.isLightTurn === true}
        state={gameState.state}
        mode={gameState.mode}
      />

      {/* Left Panel */}
      <div className={styles.leftPanel} style={{ pointerEvents: 'auto', zIndex: 100 }}>
        <div className={styles.leftPanelContent}>
          <h1 style={{
            color: 'var(--sandbox-text-celestial)',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif",
            fontSize: '14px',
            fontWeight: 300,
            marginTop: 0,
            marginBottom: '30px',
            textAlign: 'center',
            letterSpacing: '6px',
            textTransform: 'uppercase' as const,
            textShadow: '0 0 10px var(--sandbox-accent-glow)',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            lineHeight: 1.8,
          }}>
            <span style={{ fontSize: '8px', color: 'var(--sandbox-star-color)', marginBottom: '2px' }}>{'\u2726'}</span>
            <span>Puzzle</span>
            <span>Designer</span>
            <span style={{ fontSize: '8px', color: 'var(--sandbox-star-color)', marginTop: '2px' }}>{'\u2726'}</span>
          </h1>

          <div className={styles.description}>
            <p>Design and test puzzle positions for Zodiac Chess.</p>
          </div>

          <button
            className={styles.clearBoardButton}
            onClick={handleClearBoard}
            title={t('sandbox.clearBoard')}
          >
            {t('sandbox.clearBoard')}
          </button>

          <button
            className={styles.clearBoardButton}
            onClick={() => piecePaletteRef.current?.tidyPieces()}
            title="Tidy Sandbox"
          >
            Tidy Sandbox
          </button>
        </div>
      </div>

      {/* Game Area */}
      <div className={styles.gameArea}>
        <div className={styles.gameAreaContent}>
          {/* Sand image to the left of the board with piece palette overlay */}
          <div className={`${styles.sandImageContainer} ${mobileView !== 'board' ? styles.mobileHidden : ''}`}>
            <img
              src="/images/sand_no_border.png"
              alt="Sand texture"
              className={styles.sandImage}
            />
            <PiecePalette ref={piecePaletteRef} onBoardPieceDroppedToSandbox={handleBoardPieceDroppedToSandbox} />
          </div>

          <div className={`${styles.boardContainer} ${mobileView !== 'board' ? styles.mobileHidden : ''}`}>
            <Board
              gameState={gameState}
              uiState={{
                potentialMoves: potentialMoves,
                potentialSideEffects: potentialSideEffects,
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onSquareClick={handleSquareClick}
              onPieceDragStart={handlePieceDragStart}
              designateMode={!!(kingAssignMode || activeStepTarget)}
            />
          </div>

          {/* Right Panel — Puzzle Designer Controls + Saved Puzzles */}
          <div className={`${styles.rightPanel} ${mobileView === 'board' ? styles.mobileHidden : ''}`}>
            {/* Puzzle Type — shared across both columns */}
            <div className={styles.puzzleTypeToggle}>
              <div className={styles.puzzleTypeLabel}>Puzzle Type</div>
              <div className={styles.puzzleAssignRow}>
                <button
                  className={`${styles.puzzleTypeOption} ${puzzleType === 'bestMove' ? styles.active : ''}`}
                  onClick={() => setPuzzleType('bestMove')}
                  style={{ flex: 1 }}
                >
                  Find the Best Move
                </button>
                <button
                  className={`${styles.puzzleTypeOption} ${puzzleType === 'kingGuesser' ? styles.active : ''}`}
                  onClick={() => setPuzzleType('kingGuesser')}
                  style={{ flex: 1 }}
                >
                  King Guesser
                </button>
              </div>
            </div>

            {/* Two-column layout */}
            <div className={styles.puzzleDesignerColumns}>
              {/* Left column: Generator Controls */}
              <div className={`${styles.puzzleGeneratorPanel} ${mobileView === 'saved' ? styles.mobileHidden : ''}`}>
                {/* Puzzle Name */}
                <div className={styles.puzzleSection}>
                  <div className={styles.puzzleSectionLabel}>Puzzle Name</div>
                  <input
                    type="text"
                    className={styles.puzzleNameInput}
                    placeholder="Enter puzzle name..."
                    value={puzzleName}
                    onChange={(e) => setPuzzleName(e.target.value)}
                  />
                </div>

                {/* Player Side */}
                <div className={styles.puzzleSection}>
                  <div className={styles.puzzleSectionLabel}>Player Side</div>
                  <div className={styles.puzzleAssignRow}>
                    <button
                      className={`${styles.puzzleTypeOption} ${playerIsLight ? styles.active : ''}`}
                      onClick={() => setPlayerIsLight(true)}
                      style={{ flex: 1 }}
                    >
                      Light
                    </button>
                    <button
                      className={`${styles.puzzleTypeOption} ${!playerIsLight ? styles.active : ''}`}
                      onClick={() => setPlayerIsLight(false)}
                      style={{ flex: 1 }}
                    >
                      Dark
                    </button>
                  </div>
                </div>

                {/* King Assignment */}
                <div className={styles.puzzleSection}>
                  <div className={styles.puzzleSectionLabel}>King Assignment</div>
                  <div className={styles.puzzleAssignRow}>
                    <button
                      className={`${styles.puzzleAssignButton} ${kingAssignMode === 'light' ? styles.waiting : ''} ${lightKingType ? styles.assigned : ''}`}
                      onClick={() => setKingAssignMode(kingAssignMode === 'light' ? null : 'light')}
                    >
                      {kingAssignMode === 'light' ? 'Click a light piece...' : 'Light King'}
                    </button>
                    <span className={styles.puzzleAssignedValue}>{lightKingType || '—'}</span>
                  </div>
                  <div className={styles.puzzleAssignRow}>
                    <button
                      className={`${styles.puzzleAssignButton} ${kingAssignMode === 'dark' ? styles.waiting : ''} ${darkKingType ? styles.assigned : ''}`}
                      onClick={() => setKingAssignMode(kingAssignMode === 'dark' ? null : 'dark')}
                    >
                      {kingAssignMode === 'dark' ? 'Click a dark piece...' : 'Dark King'}
                    </button>
                    <span className={styles.puzzleAssignedValue}>{darkKingType || '—'}</span>
                  </div>
                </div>

                {/* Move sequence — shared by both puzzle types */}
                <div className={styles.puzzleSection}>
                  <div className={styles.puzzleSectionLabel}>
                    {puzzleType === 'bestMove' ? 'Correct Moves' : 'Replay Moves'}
                  </div>
                  {/* Total moves counter */}
                  <div className={styles.totalMovesRow}>
                    <button
                      className={styles.totalMovesButton}
                      disabled={totalMoves <= 1}
                      onClick={() => {
                        const n = totalMoves - 1;
                        setTotalMoves(n);
                        setMoveSequence(prev => prev.slice(0, n));
                      }}
                    >−</button>
                    <span className={styles.totalMovesCounter}>{totalMoves}</span>
                    <button
                      className={styles.totalMovesButton}
                      disabled={totalMoves >= (puzzleType === 'bestMove' ? 5 : 10)}
                      onClick={() => {
                        const n = totalMoves + 1;
                        setTotalMoves(n);
                        setMoveSequence(prev => {
                          const next = [...prev];
                          while (next.length < n) next.push({});
                          return next;
                        });
                      }}
                    >+</button>
                  </div>

                  {/* Position preview selector */}
                  {puzzleType === 'bestMove' && editingPuzzleId && startingFen && (
                    <div className={styles.positionSelector}>
                      <div className={styles.puzzleSectionLabel}>Board Position</div>
                      <div className={styles.positionButtons}>
                        <button
                          className={`${styles.positionButton} ${previewMoveIndex === -1 ? styles.positionButtonActive : ''}`}
                          onClick={() => previewPosition(-1)}
                        >
                          Start
                        </button>
                        {Array.from({ length: totalMoves }, (_, i) => (
                          <React.Fragment key={i}>
                            <div className={styles.positionConnector} />
                            <button
                              className={`${styles.positionButton} ${previewMoveIndex === i ? styles.positionButtonActive : ''}`}
                              onClick={() => previewPosition(i)}
                            >
                              {i + 1}
                            </button>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Move round cards */}
                  {Array.from({ length: totalMoves }, (_, i) => {
                    const round = moveSequence[i] || {};
                    const isLast = i === totalMoves - 1;
                    const pm = round.playerMove;
                    const opp = round.opponentResponse;
                    return (
                      <div key={i} className={`${styles.moveRoundCard} ${previewMoveIndex === i ? styles.moveRoundCardActive : ''}`}>
                        <div className={styles.moveRoundLabel} onClick={() => previewPosition(i)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>Move {i + 1}{isLast && totalMoves > 1 ? ' (Final)' : ''}</span>
                          {isSectionModified(i) && (
                            <span className={styles.modifiedBadge}>modified</span>
                          )}
                        </div>
                        {/* Player move (Best Move) or single move (King Guesser) */}
                        {puzzleType === 'bestMove' && (
                          <div className={styles.moveRoundSubLabel}>Player</div>
                        )}
                        <div className={styles.moveStepRow}>
                          <button
                            className={`${styles.puzzleAssignButton} ${activeStepIndex === i && activeStepTarget === 'playerFrom' ? styles.waiting : ''} ${pm?.pieceType ? styles.assigned : ''}`}
                            onClick={() => {
                              if (activeStepIndex === i && activeStepTarget === 'playerFrom') {
                                setActiveStepTarget(null);
                              } else {
                                setActiveStepIndex(i);
                                setActiveStepTarget('playerFrom');
                                if (startingFen && previewMoveIndex !== i - 1) previewPosition(i - 1);
                              }
                            }}
                          >
                            {activeStepIndex === i && activeStepTarget === 'playerFrom' ? 'Click a piece...' : 'Piece'}
                          </button>
                          <span className={styles.puzzleAssignedValue}>
                            {pm?.pieceType ? `${pm.pieceType} [${toAlgebraic(pm.from.row, pm.from.col)}]` : '—'}
                          </span>
                          <span className={styles.moveStepArrow}>→</span>
                          <button
                            className={`${styles.puzzleAssignButton} ${activeStepIndex === i && activeStepTarget === 'playerTo' ? styles.waiting : ''} ${pm?.to?.row !== undefined ? styles.assigned : ''}`}
                            onClick={() => {
                              if (activeStepIndex === i && activeStepTarget === 'playerTo') {
                                setActiveStepTarget(null);
                              } else {
                                setActiveStepIndex(i);
                                setActiveStepTarget('playerTo');
                                if (startingFen && previewMoveIndex !== i - 1) previewPosition(i - 1);
                              }
                            }}
                          >
                            {activeStepIndex === i && activeStepTarget === 'playerTo' ? 'Click a square...' : 'Dest'}
                          </button>
                          <span className={styles.puzzleAssignedValue}>
                            {pm?.to ? `[${toAlgebraic(pm.to.row, pm.to.col)}]` : '—'}
                          </span>
                        </div>

                        {/* Alternative correct moves — Best Move only */}
                        {puzzleType === 'bestMove' && (
                          <>
                            {(round.alternativePlayerMoves || []).map((alt: any, ai: number) => (
                              <div key={ai} className={styles.moveStepRow}>
                                <button
                                  className={`${styles.puzzleAssignButton} ${activeStepIndex === i && activeStepTarget === 'altFrom' && activeAltIndex === ai ? styles.waiting : ''} ${alt?.pieceType ? styles.assigned : ''}`}
                                  onClick={() => {
                                    if (activeStepIndex === i && activeStepTarget === 'altFrom' && activeAltIndex === ai) {
                                      setActiveStepTarget(null);
                                    } else {
                                      setActiveStepIndex(i);
                                      setActiveAltIndex(ai);
                                      setActiveStepTarget('altFrom');
                                    }
                                  }}
                                >
                                  {activeStepIndex === i && activeStepTarget === 'altFrom' && activeAltIndex === ai ? 'Click a piece...' : 'Piece'}
                                </button>
                                <span className={styles.puzzleAssignedValue}>
                                  {alt?.pieceType ? `${alt.pieceType} [${toAlgebraic(alt.from.row, alt.from.col)}]` : '—'}
                                </span>
                                <span className={styles.moveStepArrow}>→</span>
                                <button
                                  className={`${styles.puzzleAssignButton} ${activeStepIndex === i && activeStepTarget === 'altTo' && activeAltIndex === ai ? styles.waiting : ''} ${alt?.to?.row !== undefined ? styles.assigned : ''}`}
                                  onClick={() => {
                                    if (activeStepIndex === i && activeStepTarget === 'altTo' && activeAltIndex === ai) {
                                      setActiveStepTarget(null);
                                    } else {
                                      setActiveStepIndex(i);
                                      setActiveAltIndex(ai);
                                      setActiveStepTarget('altTo');
                                    }
                                  }}
                                >
                                  {activeStepIndex === i && activeStepTarget === 'altTo' && activeAltIndex === ai ? 'Click a square...' : 'Dest'}
                                </button>
                                <span className={styles.puzzleAssignedValue}>
                                  {alt?.to ? `[${toAlgebraic(alt.to.row, alt.to.col)}]` : '—'}
                                </span>
                                <button
                                  className={styles.altRemoveButton}
                                  onClick={() => {
                                    const seq = [...moveSequence];
                                    const alts = [...(seq[i].alternativePlayerMoves || [])];
                                    alts.splice(ai, 1);
                                    seq[i] = { ...seq[i], alternativePlayerMoves: alts.length > 0 ? alts : undefined };
                                    setMoveSequence(seq);
                                    if (activeStepIndex === i && (activeStepTarget === 'altFrom' || activeStepTarget === 'altTo') && activeAltIndex === ai) {
                                      setActiveStepTarget(null);
                                    }
                                  }}
                                  title="Remove this alternative"
                                >X</button>
                              </div>
                            ))}
                            <button
                              className={styles.addAltButton}
                              onClick={() => {
                                const seq = [...moveSequence];
                                const alts = [...(seq[i].alternativePlayerMoves || [])];
                                alts.push({} as any);
                                seq[i] = { ...seq[i], alternativePlayerMoves: alts };
                                setMoveSequence(seq);
                                setActiveStepIndex(i);
                                setActiveAltIndex(alts.length - 1);
                                setActiveStepTarget('altFrom');
                              }}
                            >+ Add correct move</button>
                          </>
                        )}

                        {/* Opponent response — Best Move only, not on final move */}
                        {puzzleType === 'bestMove' && !isLast && (
                          <>
                            <div className={styles.moveRoundSubLabel}>Opponent Response</div>
                            <div className={styles.moveStepRow}>
                              <button
                                className={`${styles.puzzleAssignButton} ${activeStepIndex === i && activeStepTarget === 'opponentFrom' ? styles.waiting : ''} ${opp?.pieceType ? styles.assigned : ''}`}
                                onClick={() => {
                                  if (activeStepIndex === i && activeStepTarget === 'opponentFrom') {
                                    setActiveStepTarget(null);
                                  } else {
                                    setActiveStepIndex(i);
                                    setActiveStepTarget('opponentFrom');
                                    if (startingFen && previewMoveIndex !== i - 1) previewPosition(i - 1);
                                  }
                                }}
                              >
                                {activeStepIndex === i && activeStepTarget === 'opponentFrom' ? 'Click a piece...' : 'Piece'}
                              </button>
                              <span className={styles.puzzleAssignedValue}>
                                {opp?.pieceType ? `${opp.pieceType} [${toAlgebraic(opp.from.row, opp.from.col)}]` : '—'}
                              </span>
                              <span className={styles.moveStepArrow}>→</span>
                              <button
                                className={`${styles.puzzleAssignButton} ${activeStepIndex === i && activeStepTarget === 'opponentTo' ? styles.waiting : ''} ${opp?.to?.row !== undefined ? styles.assigned : ''}`}
                                onClick={() => {
                                  if (activeStepIndex === i && activeStepTarget === 'opponentTo') {
                                    setActiveStepTarget(null);
                                  } else {
                                    setActiveStepIndex(i);
                                    setActiveStepTarget('opponentTo');
                                    if (startingFen && previewMoveIndex !== i - 1) previewPosition(i - 1);
                                  }
                                }}
                              >
                                {activeStepIndex === i && activeStepTarget === 'opponentTo' ? 'Click a square...' : 'Dest'}
                              </button>
                              <span className={styles.puzzleAssignedValue}>
                                {opp?.to ? `[${toAlgebraic(opp.to.row, opp.to.col)}]` : '—'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* New Puzzle button — shown when editing an existing puzzle */}
                {editingPuzzleId && (
                  <button
                    className={styles.clearBoardButton}
                    onClick={handleNewPuzzle}
                    style={{ marginTop: '10px' }}
                  >
                    New Puzzle
                  </button>
                )}

                {/* Save / Update Button */}
                <button
                  className={styles.puzzleSaveButton}
                  onClick={handleSave}
                  disabled={!canSave()}
                >
                  Save Puzzle
                </button>
                {saveStatus && (
                  <div className={`${styles.puzzleSaveFeedback} ${saveStatus.type === 'success' ? styles.success : styles.error}`}>
                    {saveStatus.message}
                  </div>
                )}
              </div>

              {/* Right column: Saved Puzzles */}
              <div className={`${styles.savedPuzzlesPanel} ${mobileView === 'designer' ? styles.mobileHidden : ''}`}>
                <div className={styles.puzzleSectionLabel}>
                  Saved Puzzles {!loadingPuzzles && `(${savedPuzzles.length})`}
                </div>
                <div className={styles.savedPuzzlesList}>
                  {loadingPuzzles && (
                    <div className={styles.savedPuzzlesEmpty}>Loading...</div>
                  )}
                  {!loadingPuzzles && savedPuzzles.length === 0 && (
                    <div className={styles.savedPuzzlesEmpty}>No puzzles saved yet</div>
                  )}
                  {!loadingPuzzles && savedPuzzles.map((puzzle) => (
                    <div
                      key={puzzle.id}
                      className={`${styles.savedPuzzleItem} ${editingPuzzleId === puzzle.id ? styles.savedPuzzleItemActive : ''}`}
                      onClick={() => handleLoadPuzzle(puzzle)}
                    >
                      <div className={styles.savedPuzzleItemHeader}>
                        <div className={styles.savedPuzzleItemName}>{puzzle.name}</div>
                        <div className={styles.savedPuzzleItemActions}>
                          {deletingPuzzleId === puzzle.id ? (
                            <>
                              <button
                                className={styles.savedPuzzleDeleteConfirm}
                                onClick={(e) => { e.stopPropagation(); handleDeletePuzzle(puzzle.id); }}
                              >
                                Confirm
                              </button>
                              <button
                                className={styles.savedPuzzleDeleteCancel}
                                onClick={(e) => { e.stopPropagation(); setDeletingPuzzleId(null); }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              className={styles.savedPuzzleDeleteButton}
                              onClick={(e) => { e.stopPropagation(); setDeletingPuzzleId(puzzle.id); }}
                              title="Delete puzzle"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      </div>
                      <div className={styles.savedPuzzleItemMeta}>
                        {puzzle.playerIsLight ? 'Light' : 'Dark'} &middot; Kings: {puzzle.lightKing}/{puzzle.darkKing}
                        {puzzle.totalMoves && puzzle.totalMoves > 1 ? ` · ${puzzle.totalMoves} moves` : ''}
                        {puzzle.movePieceType && ` · ${puzzle.movePieceType}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      </div>
    </AdSenseLayout>
  );
}

export default PuzzleDesigner;
